/**
 * Anker-Navigation
 * Verwaltet das Scrollen zu Ankern mit Integration von Lazyload und Headroom.
 * Verhindert Scroll-Sprünge durch nachgeladene Bilder und ermöglicht sanfte Navigation.
 */

export function initAnchorNavigation(doc = document) {
  // Status-Variablen für den Anchor-Jump
  let isAnchorJumping = false;     // Gibt an, ob gerade ein Anker-Sprung aktiv ist
  let targetPosition = 0;           // Ziel-Scroll-Position in Pixeln
  let cleanupTimeout = null;        // Timeout für verzögertes Cleanup nach Scroll-Ende
  let resizeObserver = null;        // Observer für Größenänderungen durch Lazyload
  let resizeDebounceFrame = null;   // RequestAnimationFrame ID für Debouncing
  let wheelListener = null;         // Referenz zum Mausrad-Event-Listener
  let touchListener = null;         // Referenz zum Touch-Event-Listener
  let scrollListener = null;        // Referenz zum Scroll-Event-Listener

  /**
   * Beendet den Anchor-Jump und räumt alle Ressourcen auf
   * - Reaktiviert Lazyload für Bilder
   * - Entfernt Headroom-Status vom Header
   * - Disconnected ResizeObserver
   * - Entfernt alle Event-Listener
   */
  function endAnchorJump() {
    if (!isAnchorJumping) return;
    isAnchorJumping = false;
    
    // Lazyload für alle pausierten Bilder wieder aktivieren
    doc.querySelectorAll('img.pause-lazyloading').forEach(img => {
      img.classList.remove('pause-lazyloading');
      img.classList.add('lazyload');
    });
    
    // Headroom-Scroll-Blocker vom Header entfernen
    const header = doc.querySelector('#siteHeader, header');
    if (header) {
      header.classList.remove('activeAnchorScroll');
    }
    
    // ResizeObserver aufräumen um Memory Leaks zu vermeiden
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    
    // Debounce-Frame abbrechen falls noch ausstehend
    if (resizeDebounceFrame) {
      cancelAnimationFrame(resizeDebounceFrame);
      resizeDebounceFrame = null;
    }
    
    // Alle Event-Listener sauber entfernen
    if (wheelListener) {
      window.removeEventListener('wheel', wheelListener);
      wheelListener = null;
    }
    if (touchListener) {
      window.removeEventListener('touchstart', touchListener);
      touchListener = null;
    }
    if (scrollListener) {
      window.removeEventListener('scroll', scrollListener);
      scrollListener = null;
    }
  }

  /**
   * Wird aufgerufen, wenn der Benutzer während des Anchor-Jumps interagiert
   * (Mausrad oder Touch) - beendet den automatischen Scroll sofort
   */
  function handleUserInteraction() {
    const header = doc.querySelector('#siteHeader, header');
    if (header) {
      header.classList.remove('activeAnchorScroll');
    }
    endAnchorJump();
  }

  /**
   * Behandelt Scroll-Events während des Anchor-Jumps
   * Wartet 250ms nach dem letzten Scroll, bevor aufgeräumt wird
   */
  function handleScrollDuringJump() {
    clearTimeout(cleanupTimeout);
    cleanupTimeout = setTimeout(endAnchorJump, 250);
  }

  /**
   * Hauptfunktion: Scrollt zum Ziel-Anker und überwacht Positionsänderungen
   * 
   * Ablauf:
   * 1. Prüft ob ein gültiger Hash existiert
   * 2. Pausiert Lazyload um Scroll-Sprünge zu vermeiden
   * 3. Aktiviert Headroom-Blocker am Header
   * 4. Scrollt sofort zur Ziel-Position
   * 5. Überwacht Größenänderungen und passt Position automatisch an
   * 6. Wartet auf Benutzer-Interaktion zum Beenden
   */
  function scrollToAnchor() {
    const hash = window.location.hash;
    if (!hash || hash === '#') return false;

    const targetElement = doc.querySelector(hash);
    if (!targetElement) return false;

    // Vorherigen Jump abbrechen falls noch aktiv (verhindert Memory Leaks)
    endAnchorJump();
    
    isAnchorJumping = true;

    // Lazyload für ALLE Bilder pausieren während des Scrollens
    // Verhindert, dass nachgeladene Bilder die Scroll-Position verschieben
    doc.querySelectorAll('img.lazyload').forEach(img => {
      img.classList.remove('lazyload');
      img.classList.add('pause-lazyloading');
    });

    // Headroom am Header deaktivieren während des Anchor-Jumps
    // Verhindert störende Header-Animationen beim Scrollen
    const header = doc.querySelector('#siteHeader, header');
    if (header) {
      header.classList.add('activeAnchorScroll');
    }

    // Initiale Position berechnen und sanft dorthin scrollen
    targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: targetPosition, behavior: 'smooth' });

    // ResizeObserver überwacht Größenänderungen durch nachgeladene Inhalte
    // Wenn sich die Position des Ziels ändert, wird automatisch nachkorrigiert
    // Debouncing via requestAnimationFrame verhindert zu viele Scroll-Aufrufe
    resizeObserver = new ResizeObserver(() => {
      if (!isAnchorJumping) return;

      // Vorherige Animation abbrechen falls noch ausstehend
      if (resizeDebounceFrame) {
        cancelAnimationFrame(resizeDebounceFrame);
      }

      // Neue Position im nächsten Animation Frame berechnen und anpassen
      resizeDebounceFrame = requestAnimationFrame(() => {
        if (!isAnchorJumping) return;

        const newTargetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;

        // Nur korrigieren wenn die Abweichung größer als 1px ist (Performance)
        if (Math.abs(targetPosition - newTargetPosition) > 1) {
          targetPosition = newTargetPosition;
          window.scrollTo({ top: targetPosition, behavior: 'instant' });
        }

        resizeDebounceFrame = null;
      });
    });

    // Ziel-Element und alle Eltern-Container beobachten (Performance-optimiert)
    // Statt alle 100+ Bilder zu beobachten, nur die relevanten Container
    resizeObserver.observe(targetElement);
    
    // Parent-Container bis zum Body hochgehen und beobachten
    let parent = targetElement.parentElement;
    while (parent && parent !== doc.body) {
      resizeObserver.observe(parent);
      parent = parent.parentElement;
    }

    // Event-Listener für Benutzer-Interaktion registrieren
    // Bei Mausrad/Touch wird der automatische Scroll sofort beendet
    wheelListener = handleUserInteraction;
    touchListener = handleUserInteraction;
    scrollListener = handleScrollDuringJump;
    
    window.addEventListener('wheel', wheelListener, { passive: true });
    window.addEventListener('touchstart', touchListener, { passive: true });
    window.addEventListener('scroll', scrollListener, { passive: true });

    return true;
  }

  // ========================================================================
  // Initialisierung: Event-Listener für verschiedene Anker-Navigation-Szenarien
  // ========================================================================

  // Szenario 1: Hash ist bereits in der URL beim Laden der Seite
  // (z.B. durch Link von anderer Seite: https://example.com/page#section)
  if (window.location.hash) {
    let scrollAttempted = false;
    
    // Versuch 1: Nach vollständigem Laden aller Ressourcen (Bilder, CSS, JS)
    window.addEventListener('load', () => {
      if (!scrollAttempted) {
        setTimeout(() => {
          scrollAttempted = scrollToAnchor();
        }, 100);
      }
    }, { once: true });
    
    // Versuch 2 (Fallback): Wenn DOM bereit ist, aber Ressourcen noch laden
    // Längeres Delay um sicherzustellen dass Layout stabil ist
    document.addEventListener('DOMContentLoaded', () => {
      if (!scrollAttempted) {
        setTimeout(() => {
          scrollAttempted = scrollToAnchor();
        }, 300);
      }
    }, { once: true });
  }

  // Szenario 2: Hash ändert sich nach dem Laden (Browser Back/Forward oder programmatisch)
  window.addEventListener('hashchange', () => {
    scrollToAnchor();
  });

  // Szenario 3: Benutzer klickt auf Anker-Link innerhalb der Seite
  doc.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || !href.includes('#')) return;

    const [path, hash] = href.split('#');
    if (!hash) return;
    
    // Prüfen ob Link zur aktuellen Seite oder Homepage zeigt
    const isHomepageHash = (path === '/' || path === '' || path === window.location.pathname);
    const currentPageIsHome = window.location.pathname === '/' || window.location.pathname === '/index.html';
    
    // Nur Same-Page-Links oder Homepage-Links verarbeiten
    if (isHomepageHash || (path === '/' && !currentPageIsHome)) {
      e.preventDefault();
      
      // Fall A: Von anderer Seite zur Homepage mit Hash navigieren
      // Muss komplette Navigation auslösen (nicht nur Scroll)
      if (!currentPageIsHome && path === '/') {
        window.location.href = '/#' + hash;
        return;
      }
      
      // Fall B: Same-Page-Navigation - Hash setzen triggert automatisch hashchange Event
      // hashchange Event ruft dann scrollToAnchor() auf (siehe oben)
      if (window.location.hash !== '#' + hash) {
        window.location.hash = hash;
      } else {
        // Fall C: Bereits beim gleichen Hash - manuell nochmal scrollen
        scrollToAnchor();
      }
    }
  });
}
